import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import PublishedWithChangesOutlinedIcon from "@mui/icons-material/PublishedWithChangesOutlined";
import { MEDICAL_DEPLOYMENT_LABELS } from "constants/dashboardTerminology";
import {
  getAllowedOperationalTransitions,
  getEstadoOperativoVisual,
  getOperationalOriginLabel,
  isTerminalOperationalStatus,
  normalizeOperationalStatus,
} from "utils/operationalStatus";

const DEFAULT_ALLOWED_ROLES = ["1", "2", "6", "8"];

const transitionActions = {
  en_preparacion: {
    label: "Pasar a preparación",
    color: "warning",
    message: "Esta acción registrará la preparación del despliegue y quedará guardada en el historial.",
  },
  desplegado: {
    label: "Marcar como desplegado",
    color: "success",
    message: "Esta acción registrará el inicio del despliegue y quedará guardada en el historial.",
  },
  cerrado: {
    label: "Cerrar despliegue",
    color: "info",
    message: "Al cerrar el despliegue, la convocatoria quedará finalizada.",
  },
  cancelado: {
    label: "Cancelar despliegue",
    color: "error",
    message:
      "Al cancelar el despliegue, la convocatoria dejará de estar activa y no admitirá nuevos participantes.",
  },
};

const getStoredRole = () => {
  try {
    return String(JSON.parse(localStorage.getItem("token") || "{}")?.rol || "");
  } catch (error) {
    return "";
  }
};

const formatDate = (value, includeTime = false) => {
  if (!value) return "-";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/);
  if (!match) return String(value);

  const date = `${match[3]}/${match[2]}/${match[1]}`;
  return includeTime && match[4] ? `${date} ${match[4]}:${match[5]}` : date;
};

const getRequestError = (requestError) => {
  const status = requestError?.response?.status;
  const rawResponseData = requestError?.response?.data || {};
  const responseData = rawResponseData?.data || rawResponseData;
  const backendMessage = responseData.message || responseData.error;

  if (status === 400) return backendMessage || "Revise el estado y el motivo enviados.";
  if (status === 401) return "No fue posible autenticar la solicitud.";
  if (status === 403) return "No tiene permiso para modificar este despliegue.";
  if (status === 404) return "El despliegue no fue encontrado.";
  if (status === 409) return backendMessage || "La transición solicitada ya no está disponible.";
  return "No se pudo actualizar el estado operativo.";
};

const EstadoOperativoDialog = ({
  open,
  onClose,
  convocatoria,
  onUpdated,
  allowedRoles = DEFAULT_ALLOWED_ROLES,
}) => {
  const idConvocatoria =
    convocatoria?.idConvocatoria ?? convocatoria?.id_convocatoria ?? convocatoria?.id;
  const [currentConvocatoria, setCurrentConvocatoria] = useState({});
  const [selectedState, setSelectedState] = useState("");
  const [motivo, setMotivo] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [successSeverity, setSuccessSeverity] = useState("success");
  const [conflict, setConflict] = useState(null);

  const currentRole = getStoredRole();
  const canManage = allowedRoles.map(String).includes(currentRole);
  const idIsValid = Number.isFinite(Number(idConvocatoria)) && Number(idConvocatoria) > 0;

  useEffect(() => {
    if (!open) return;

    setCurrentConvocatoria(convocatoria || {});
    setSelectedState("");
    setMotivo("");
    setSaving(false);
    setError("");
    setSuccessMessage("");
    setConflict(null);
  }, [convocatoria, idConvocatoria, open]);

  const currentState = normalizeOperationalStatus(
    currentConvocatoria.estadoOperativo ?? currentConvocatoria.estado_operativo
  );
  const currentVisual = getEstadoOperativoVisual(currentState);
  const allowedTransitions = useMemo(
    () => getAllowedOperationalTransitions(currentState),
    [currentState]
  );
  const selectedVisual = getEstadoOperativoVisual(selectedState);
  const selectedAction = transitionActions[selectedState];
  const cancellationRequiresReason = selectedState === "cancelado";
  const isTerminal = isTerminalOperationalStatus(currentState);
  const canConfirm =
    Boolean(selectedState) && !saving && (!cancellationRequiresReason || Boolean(motivo.trim()));

  const handleSelectTransition = (state) => {
    setSelectedState(state);
    setError("");
    setSuccessMessage("");
    setConflict(null);
  };

  const handleConfirm = async () => {
    if (!canConfirm || !idIsValid || !canManage) return;

    try {
      setSaving(true);
      setError("");
      setSuccessMessage("");
      setConflict(null);

      const response = await axios.patch(
        `/dashboard-operativo/convocatorias/${idConvocatoria}/estado-operativo`,
        {
          estadoOperativo: selectedState,
          motivo: motivo.trim(),
        }
      );
      const payload = response?.data?.data || response?.data || {};
      const updatedConvocatoria =
        payload.convocatoria && Object.keys(payload.convocatoria).length
          ? { ...currentConvocatoria, ...payload.convocatoria }
          : currentConvocatoria;

      setCurrentConvocatoria(updatedConvocatoria);
      setSuccessMessage(payload.message || "Estado operativo actualizado correctamente.");
      setSuccessSeverity(payload.changed === false ? "info" : "success");
      setSelectedState("");
      setMotivo("");
      await Promise.resolve(onUpdated?.(updatedConvocatoria)).catch(() => undefined);
    } catch (requestError) {
      const status = requestError?.response?.status;
      const rawResponseData = requestError?.response?.data || {};
      const responseData = rawResponseData?.data || rawResponseData;

      setError(getRequestError(requestError));

      if (status === 409) {
        if (responseData.estadoActual) {
          setCurrentConvocatoria((current) => ({
            ...current,
            estadoOperativo: responseData.estadoActual,
          }));
        }
        setSelectedState("");
        setMotivo("");
        setConflict({
          estadoActual: responseData.estadoActual,
          transicionesPermitidas: Array.isArray(responseData.transicionesPermitidas)
            ? responseData.transicionesPermitidas
            : [],
        });
        await Promise.resolve(onUpdated?.()).catch(() => undefined);
      }
    } finally {
      setSaving(false);
    }
  };

  const deploymentName =
    currentConvocatoria.descripcionConvo ||
    currentConvocatoria.nombreJornada ||
    currentConvocatoria.nombre_jornada ||
    `${MEDICAL_DEPLOYMENT_LABELS.singularShort} sin nombre`;
  const projectName =
    currentConvocatoria.nombreProyecto ||
    currentConvocatoria.nombre_proyecto ||
    currentConvocatoria.codigoProyecto ||
    currentConvocatoria.codigo_proyecto ||
    "Sin proyecto";
  const projectCode =
    currentConvocatoria.codigoProyectoVisual ||
    currentConvocatoria.codigo_proyecto_visual ||
    currentConvocatoria.codigoProyecto ||
    currentConvocatoria.codigo_proyecto;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 7 }}>
        <Typography variant="h4" component="div" sx={{ fontWeight: 900 }}>
          Gestionar estado operativo
        </Typography>
        <Tooltip title="Cerrar">
          <span>
            <IconButton
              aria-label="Cerrar gestión de estado"
              onClick={onClose}
              disabled={saving}
              sx={{ position: "absolute", right: 12, top: 12 }}
            >
              <CloseOutlinedIcon />
            </IconButton>
          </span>
        </Tooltip>
      </DialogTitle>

      <DialogContent dividers>
        {!canManage ? (
          <Alert severity="warning">No tiene permiso para modificar este despliegue.</Alert>
        ) : !idIsValid ? (
          <Alert severity="error">No se encontró el despliegue seleccionado.</Alert>
        ) : (
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>
                {deploymentName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {[projectName, projectCode].filter(Boolean).join(" · ")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {[
                  formatDate(currentConvocatoria.fecha || currentConvocatoria.fechaJornada),
                  currentConvocatoria.comunidad,
                ]
                  .filter((value) => value && value !== "-")
                  .join(" · ")}
              </Typography>
            </Box>

            <Paper
              variant="outlined"
              sx={{ p: 2, borderLeft: `5px solid ${currentVisual.border}`, bgcolor: currentVisual.background }}
            >
              <Stack spacing={1}>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
                  <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                    Estado operativo actual
                  </Typography>
                  <Chip label={currentVisual.label} color={currentVisual.chipColor} variant="filled" />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Origen: {getOperationalOriginLabel(
                    currentConvocatoria.estadoOperativoOrigen ?? currentConvocatoria.estado_operativo_origen
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Última actualización: {formatDate(
                    currentConvocatoria.actualizadoEn ?? currentConvocatoria.actualizado_en,
                    true
                  )}
                </Typography>
                {(currentConvocatoria.motivo || currentConvocatoria.motivoEstadoOperativo) && (
                  <Typography variant="body2">
                    Motivo anterior: {currentConvocatoria.motivo || currentConvocatoria.motivoEstadoOperativo}
                  </Typography>
                )}
              </Stack>
            </Paper>

            <Divider />

            {isTerminal ? (
              <Alert severity="info">Este despliegue se encuentra en un estado terminal.</Alert>
            ) : allowedTransitions.length === 0 ? (
              <Alert severity="warning">No hay transiciones disponibles para el estado operativo actual.</Alert>
            ) : (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1 }}>
                  Acciones disponibles
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap" useFlexGap>
                  {allowedTransitions.map((state) => {
                    const action = transitionActions[state];
                    return (
                      <Button
                        key={state}
                        variant={selectedState === state ? "contained" : "outlined"}
                        color={action?.color || "primary"}
                        startIcon={<PublishedWithChangesOutlinedIcon />}
                        onClick={() => handleSelectTransition(state)}
                        disabled={saving}
                      >
                        {action?.label || getEstadoOperativoVisual(state).label}
                      </Button>
                    );
                  })}
                </Stack>
              </Box>
            )}

            {selectedState && (
              <Paper
                variant="outlined"
                sx={{ p: 2, borderLeft: `5px solid ${selectedVisual.border}` }}
              >
                <Stack spacing={2}>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Chip label={currentVisual.label} color={currentVisual.chipColor} size="small" />
                    <Typography aria-hidden="true">→</Typography>
                    <Chip label={selectedVisual.label} color={selectedVisual.chipColor} size="small" />
                  </Box>

                  <Alert severity={selectedState === "cancelado" ? "error" : selectedState === "cerrado" ? "warning" : "info"}>
                    {selectedAction?.message}
                  </Alert>

                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label={cancellationRequiresReason ? "Motivo de cancelación" : "Motivo o comentario"}
                    value={motivo}
                    onChange={(event) => setMotivo(event.target.value.slice(0, 500))}
                    required={cancellationRequiresReason}
                    disabled={saving}
                    inputProps={{ maxLength: 500 }}
                    helperText={
                      cancellationRequiresReason
                        ? `El motivo es obligatorio y quedará registrado en el historial. ${motivo.length}/500`
                        : `${motivo.length}/500`
                    }
                  />
                </Stack>
              </Paper>
            )}

            {error && <Alert severity="error">{error}</Alert>}
            {successMessage && <Alert severity={successSeverity}>{successMessage}</Alert>}
            {conflict && (
              <Alert severity="warning">
                <Stack spacing={1}>
                  <Typography variant="body2">
                    Estado actual: {getEstadoOperativoVisual(conflict.estadoActual).label}
                  </Typography>
                  {conflict.transicionesPermitidas.length > 0 && (
                    <Box display="flex" gap={0.75} flexWrap="wrap">
                      {conflict.transicionesPermitidas.map((state) => {
                        const visual = getEstadoOperativoVisual(state);
                        return <Chip key={state} label={visual.label} color={visual.chipColor} size="small" />;
                      })}
                    </Box>
                  )}
                </Stack>
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cerrar
        </Button>
        {selectedState && canManage && idIsValid && (
          <Button
            variant="contained"
            color={selectedAction?.color || "primary"}
            onClick={handleConfirm}
            disabled={!canConfirm}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <PublishedWithChangesOutlinedIcon />}
          >
            {saving ? "Guardando..." : "Confirmar cambio"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

EstadoOperativoDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  convocatoria: PropTypes.object,
  onUpdated: PropTypes.func,
  allowedRoles: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
};

export default EstadoOperativoDialog;
